import type { TextRun } from '@/api/types/stowed-element'
import { naturalLeadingPt } from '@/lib/text/leading'
import { effectivePointSizeForRun } from '@/lib/text/text-run-vertical-align'
import type { ShapeTextCharRunMap } from '../types/shape-text-layout-state'

export type CharSpan = { ch: string; run: TextRun | null; advance: number; charIndex: number }

/**
 * One span per character, not one per run. `layoutShapeTextContent` advances the caret by
 * a sum of per-character measurements, but a chunk is a single contiguous text run that
 * the browser shapes with its own metrics — nothing made the two agree, so any per-glyph
 * difference compounded left to right and, on a shape with long rows (a heart's widest
 * row holds ~30 glyphs where a star's holds 2–6), grew past half a glyph and made
 * `caretIndexAtShapeLocalPoint` return the offset before the one clicked (WMPCM-8970).
 * Giving each glyph a box of exactly its measured advance makes "painted glyph x ==
 * carets[n].x" true by construction instead of by hoping two text engines agree.
 */
export function buildCharSpans(
  chunk: { text: string; startCharIndex: number },
  charRunMap: ShapeTextCharRunMap,
  advances: readonly number[]
): CharSpan[] {
  const spans: CharSpan[] = []

  for (let i = 0; i < chunk.text.length; i++) {
    const charIndex = chunk.startCharIndex + i

    spans.push({
      ch: chunk.text[i],
      run: charRunMap[charIndex] ?? null,
      // Undefined only if the flow stopped mid-chunk, which cannot happen — every
      // character in a chunk was placed by `emit`, which is what fills `advances`.
      advance: advances[charIndex] ?? 0,
      charIndex,
    })
  }

  return spans
}

/**
 * The tallest run painted in this chunk, resolved so the block's own font can be set to
 * match it. Falls back to the frame default for an empty chunk.
 *
 * Ranked by `rowMetricPt` - the same metric `applyPerLineLeadingToFlow` used to decide
 * `chunk.rowHeight` - rather than by raw point size. The two must name the SAME run: the
 * strut is what pins this line box's baseline (see the `fontSize` comment in `ShapeTextChunk`), so
 * picking it by a different measure than the one that sized the box would put the
 * baseline somewhere the model's row height did not account for. `strutSize` itself stays
 * the run's own point size, which is what the CSS font needs.
 *
 * A span with NO run must rank on the size it will be assigned, not on `rowMetricPt`'s
 * own `DEFAULT_POINT_SIZE` fallback: those differ whenever the frame's default size is
 * not 12, and ranking one span by 14.4 while assigning it `pointSize` could hand the
 * strut to a shorter run than the one actually setting the line box's height.
 */
export function resolveChunkStrut(
  spans: readonly CharSpan[],
  pointSize: number,
  rowMetricPt: (run: TextRun) => number
): { strutRun: TextRun | null; strutSize: number } {
  let strutRun: TextRun | null = null
  let strutSize = 0
  let strutHeightPt = 0

  for (const span of spans) {
    // WMPCM-10141: `rowMetricPt` already resolves a superscript/subscript run's EFFECTIVE
    // (scaled) height (see `runRowMetricPt`, shape-text-measure.ts) — `spanSize` (the CSS
    // font-size this strut would use if it wins) must agree, or a superscript run winning
    // the strut would set a line box sized for its small ink but fonted at its full size.
    const spanSize = effectivePointSizeForRun(span.run, pointSize)
    const heightPt = span.run ? rowMetricPt(span.run) : naturalLeadingPt(spanSize)

    if (heightPt > strutHeightPt) {
      strutHeightPt = heightPt
      strutSize = spanSize
      strutRun = span.run
    }
  }

  if (strutSize === 0) strutSize = pointSize

  return { strutRun, strutSize }
}
